import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Box,
  Typography,
  Divider,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { customExportAPI } from '../../services/api';

// Available fields grouped by category
const FIELD_GROUPS = {
  'Basic Information': [
    { name: 'company_name', label: 'Company Name' },
    { name: 'company_slug', label: 'Company Slug' },
    { name: 'company_website', label: 'Company Website' },
    { name: 'recordowl_company_url', label: 'RecordOwl Company URL' },
  ],
  'Contact Information': [
    { name: 'emails', label: 'Email Addresses' },
    { name: 'phones', label: 'Phone Numbers' },
  ],
  'Business Details': [
    { name: 'business_info', label: 'Business Information' },
    { name: 'background', label: 'Background' },
    { name: 'news', label: 'News' },
    { name: 'key_people', label: 'Founders & Key Personnel' },
  ],
  'Metadata': [
    { name: 'data_quality_score', label: 'Data Quality Score' },
    { name: 'status', label: 'Processing Status' },
    { name: 'fields_completed', label: 'Fields Completed' },
    { name: 'total_fields', label: 'Total Fields' },
    { name: 'created_at', label: 'Created Date' },
    { name: 'processed_at', label: 'Processed Date' },
  ],
};

// Detail level options with descriptions
const DETAIL_LEVELS = [
  { 
    value: 'compact', 
    label: 'Compact', 
    description: 'Values only - minimal columns, one per field' 
  },
  { 
    value: 'standard', 
    label: 'Standard', 
    description: 'Values with sources - 2 columns per field' 
  },
  { 
    value: 'detailed', 
    label: 'Detailed', 
    description: 'Full expansion - separate column for each item' 
  },
];

const CustomExportDialog = ({ open, onClose, jobId, leadIds = [], singleLeadId = null }) => {
  const [selectedFields, setSelectedFields] = useState(['company_name', 'emails', 'phones']);
  const [exportFormat, setExportFormat] = useState('csv');
  const [detailLevel, setDetailLevel] = useState('compact');
  const [exporting, setExporting] = useState(false);

  const isSingleLead = singleLeadId !== null;
  const leadCount = isSingleLead ? 1 : leadIds.length;

  const allFields = useMemo(() => {
    return Object.values(FIELD_GROUPS).flat().map(f => f.name);
  }, []);

  const handleSelectAll = () => {
    setSelectedFields(allFields);
  };

  const handleDeselectAll = () => {
    setSelectedFields([]);
  };

  const handleFieldToggle = (fieldName) => {
    setSelectedFields(prev => {
      if (prev.includes(fieldName)) {
        return prev.filter(f => f !== fieldName);
      } else {
        return [...prev, fieldName];
      }
    });
  };

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      alert('Please select at least one field to export');
      return;
    }

    setExporting(true);
    try {
      let response;
      if (isSingleLead) {
        response = await customExportAPI.exportLead(jobId, singleLeadId, selectedFields, exportFormat, detailLevel);
      } else {
        response = await customExportAPI.exportBatch(jobId, leadIds, selectedFields, exportFormat, detailLevel);
      }

      // Handle the response based on format
      if (exportFormat === 'html') {
        // Open HTML in new window for printing
        const htmlBlob = new Blob([response.data], { type: 'text/html' });
        const url = URL.createObjectURL(htmlBlob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        // Download Excel or CSV file
        const blob = new Blob([response.data], {
          type: exportFormat === 'excel' 
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'text/csv'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().split('T')[0];
        link.download = `leads_export_${timestamp}.${exportFormat === 'excel' ? 'xlsx' : 'csv'}`;
        link.click();
        window.URL.revokeObjectURL(url);
      }

      onClose();
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Export Leads
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Field Selection */}
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Select Fields to Export:
        </Typography>
        
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button size="small" variant="outlined" onClick={handleDeselectAll}>
            Deselect All
          </Button>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
          {Object.entries(FIELD_GROUPS).map(([groupName, fields]) => (
            <Box key={groupName} sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                {groupName}:
              </Typography>
              <FormGroup>
                {fields.map(field => (
                  <FormControlLabel
                    key={field.name}
                    control={
                      <Checkbox
                        checked={selectedFields.includes(field.name)}
                        onChange={() => handleFieldToggle(field.name)}
                        size="small"
                      />
                    }
                    label={field.label}
                  />
                ))}
              </FormGroup>
            </Box>
          ))}
        </Paper>

        <Divider sx={{ my: 3 }} />

        {/* Export Detail Level Selection */}
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Export Detail Level:
          </FormLabel>
          <RadioGroup
            value={detailLevel}
            onChange={(e) => setDetailLevel(e.target.value)}
          >
            {DETAIL_LEVELS.map(level => (
              <FormControlLabel
                key={level.value}
                value={level.value}
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                      {level.label}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                      - {level.description}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 3 }} />

        {/* Export Format Selection */}
        <FormControl component="fieldset">
          <FormLabel component="legend">Export Format:</FormLabel>
          <RadioGroup
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value)}
          >
            <FormControlLabel value="excel" control={<Radio />} label="Excel (.xlsx)" />
            <FormControlLabel value="csv" control={<Radio />} label="CSV (.csv)" />
            <FormControlLabel value="html" control={<Radio />} label="Print (Browser)" />
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 3 }} />

        {/* Summary */}
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" color="textSecondary">
            Selection Summary:
          </Typography>
          <Typography variant="body1">
            {leadCount} lead{leadCount > 1 ? 's' : ''}, {selectedFields.length} field{selectedFields.length > 1 ? 's' : ''} selected
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            Detail Level: <strong>{DETAIL_LEVELS.find(l => l.value === detailLevel)?.label}</strong>
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={exporting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={exporting || selectedFields.length === 0}
          startIcon={<DownloadIcon />}
        >
          {exporting ? 'Exporting...' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomExportDialog;

